import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, CheckCircle2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import usePageMeta from "../../lib/usePageMeta";

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent";

const MAX_MB = 8;

const PRIMARY_REASONS = [
  { value: "learn_about_workplace_chaplaincy", label: "I want to learn about workplace chaplaincy" },
  { value: "interested_in_becoming", label: "I am interested in becoming a workplace chaplain" },
  { value: "experienced_chaplain_additional_training", label: "I am an experienced chaplain seeking additional training" },
  { value: "strengthen_corporate_skills", label: "I would like to strengthen my corporate/workplace chaplaincy skills" },
  { value: "future_ccchaps_opportunities", label: "I would like to be considered for future CCCHAPS chaplain opportunities" },
  { value: "other", label: "Other" },
];

const ACADEMY_TRACKS = [
  { value: "workplace_development", label: "Workplace Chaplain Development" },
  { value: "continuing_education", label: "Continuing Education & Professional Development" },
  { value: "not_sure", label: "I'm not sure yet" },
];

const initial = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  current_role: "",
  primary_reason: "",
  primary_reason_other: "",
  chaplain_experience: "",
  years_experience: "",
  education_training: "",
  corporate_experience: "",
  languages: "",
  training_interests: "",
  availability: "",
  additional_information: "",
  academy_track: "",
};

export default function AcademyApply() {
  usePageMeta({
    title: "Apply — Chaplain Training Academy | California Corporate Chaplains",
    description:
      "Apply to the CCCHAPS Chaplain Training Academy. Applications are open for the workplace chaplain development and continuing-education tracks.",
  });

  const [form, setForm] = useState(initial);
  const [resume, setResume] = useState(null);
  const [futureOptIn, setFutureOptIn] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setResume(null);
      return;
    }
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("Résumé must be a PDF, DOC, or DOCX file.");
      e.target.value = "";
      setResume(null);
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Résumé exceeds the ${MAX_MB} MB limit.`);
      e.target.value = "";
      setResume(null);
      return;
    }
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please confirm the required consent before submitting.");
    if (!form.primary_reason) return toast.error("Please choose your primary reason for applying.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v != null) fd.append(k, v);
      });
      fd.append("future_opportunities_optin", futureOptIn ? "true" : "false");
      fd.append("consent", "true");
      if (resume) fd.append("resume", resume);
      await api.post("/academy/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="academy-apply-success">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 text-center shadow-sm">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-50 text-green-600 mb-6">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">
            Thank you for applying to the CCCHAPS Chaplain Training Academy.
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Your application has been received and will be reviewed. A member of our team will follow up as we work through the pipeline.
          </p>
          <p className="mt-4 text-xs text-slate-500 leading-relaxed">
            Academy application, participation, or completion does not guarantee employment, placement, assignment, deployment, or a position with California Corporate Chaplains.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/academy"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
            >
              Back to the Academy
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
            >
              Return home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="academy-apply-page">
      <Link to="/academy" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to the Academy
      </Link>
      <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700 mb-4">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Applications Now Open
      </span>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
        CCCHAPS Academy — Application
      </p>
      <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
        Apply to the Chaplain Training Academy
      </h1>
      <p className="mt-3 text-slate-600 leading-relaxed">
        Share a little about yourself and your training interests. All fields marked with{" "}
        <span className="text-[#4A6FA5]">*</span> are required. Optional fields help us understand fit and next steps.
      </p>

      <div className="mt-6 rounded-xl border border-slate-200 bg-[#F8FAFC] p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700 leading-relaxed">
          Please do not include Social Security numbers, dates of birth, driver's license numbers, medical or disability information, financial information, or other highly sensitive personal information in your application or uploaded documents.
        </p>
      </div>

      <form
        onSubmit={submit}
        data-testid="academy-apply-form"
        className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-6 shadow-sm"
      >
        {/* ---- REQUIRED CORE ---- */}
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">Your details</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="First name" required>
              <input
                required
                data-testid="academy-first-name"
                value={form.first_name}
                onChange={update("first_name")}
                className={inputCls}
              />
            </Field>
            <Field label="Last name" required>
              <input
                required
                data-testid="academy-last-name"
                value={form.last_name}
                onChange={update("last_name")}
                className={inputCls}
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <Field label="Email" required>
              <input
                required
                type="email"
                data-testid="academy-email"
                value={form.email}
                onChange={update("email")}
                className={inputCls}
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                type="tel"
                data-testid="academy-phone"
                value={form.phone}
                onChange={update("phone")}
                className={inputCls}
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <Field label="City" required>
              <input
                required
                data-testid="academy-city"
                value={form.city}
                onChange={update("city")}
                className={inputCls}
              />
            </Field>
            <Field label="State" required>
              <input
                required
                data-testid="academy-state"
                value={form.state}
                onChange={update("state")}
                className={inputCls}
              />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Current role or occupation" required>
              <input
                required
                data-testid="academy-current-role"
                value={form.current_role}
                onChange={update("current_role")}
                className={inputCls}
                placeholder="e.g., Chaplain, Pastor, HR Business Partner, Nurse, Volunteer Coordinator"
              />
            </Field>
          </div>
        </div>

        {/* ---- PRIMARY REASON ---- */}
        <fieldset className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <legend className="px-1.5 text-sm font-medium text-slate-700">
            Primary reason for applying <span className="text-[#4A6FA5]">*</span>
          </legend>
          <div className="mt-2 space-y-2" data-testid="academy-primary-reasons">
            {PRIMARY_REASONS.map((r) => (
              <label
                key={r.value}
                className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 cursor-pointer hover:border-[#4A6FA5] text-sm text-slate-800"
              >
                <input
                  type="radio"
                  name="primary_reason"
                  value={r.value}
                  checked={form.primary_reason === r.value}
                  onChange={() => setForm((s) => ({ ...s, primary_reason: r.value }))}
                  data-testid={`academy-reason-${r.value}`}
                  className="mt-0.5 h-4 w-4 text-[#4A6FA5] border-slate-300 focus:ring-[#4A6FA5]"
                />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
          {form.primary_reason === "other" && (
            <div className="mt-3">
              <Field label="Please briefly describe (optional)">
                <input
                  data-testid="academy-reason-other-text"
                  value={form.primary_reason_other}
                  onChange={update("primary_reason_other")}
                  className={inputCls}
                  placeholder="Briefly describe your reason for applying"
                />
              </Field>
            </div>
          )}
        </fieldset>

        {/* ---- ACADEMY TRACK (optional) ---- */}
        <fieldset className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <legend className="px-1.5 text-sm font-medium text-slate-700">
            Which Academy track fits you best? <span className="text-slate-400 text-xs font-normal">(optional)</span>
          </legend>
          <p className="text-xs text-slate-500 mt-1 mb-2">
            This helps the review team route your application. You don't have to choose — you can decide later with our team.
          </p>
          <div className="mt-2 space-y-2" data-testid="academy-track-options">
            {ACADEMY_TRACKS.map((t) => (
              <label
                key={t.value}
                className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 cursor-pointer hover:border-[#4A6FA5] text-sm text-slate-800"
              >
                <input
                  type="radio"
                  name="academy_track"
                  value={t.value}
                  checked={form.academy_track === t.value}
                  onChange={() => setForm((s) => ({ ...s, academy_track: t.value }))}
                  data-testid={`academy-track-${t.value}`}
                  className="mt-0.5 h-4 w-4 text-[#4A6FA5] border-slate-300 focus:ring-[#4A6FA5]"
                />
                <span>{t.label}</span>
              </label>
            ))}
          </div>
          {form.academy_track && (
            <button
              type="button"
              onClick={() => setForm((s) => ({ ...s, academy_track: "" }))}
              data-testid="academy-track-clear"
              className="mt-2 text-xs text-slate-500 hover:text-[#4A6FA5] underline underline-offset-2"
            >
              Clear selection
            </button>
          )}
        </fieldset>

        {/* ---- OPTIONAL BACKGROUND ---- */}
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">
            Background & training interests <span className="text-slate-400 normal-case tracking-normal">(all optional)</span>
          </p>
          <Field label="Current chaplain, ministry, or spiritual-care experience">
            <textarea
              rows={4}
              data-testid="academy-chaplain-experience"
              value={form.chaplain_experience}
              onChange={update("chaplain_experience")}
              className={`${inputCls} resize-none`}
              placeholder="Briefly describe your relevant care/ministry experience."
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <Field label="Years of relevant experience">
              <input
                data-testid="academy-years-experience"
                value={form.years_experience}
                onChange={update("years_experience")}
                className={inputCls}
                placeholder="e.g., 3 years"
              />
            </Field>
            <Field label="Languages spoken">
              <input
                data-testid="academy-languages"
                value={form.languages}
                onChange={update("languages")}
                className={inputCls}
                placeholder="e.g., English, Spanish"
              />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Education or chaplaincy training">
              <textarea
                rows={3}
                data-testid="academy-education-training"
                value={form.education_training}
                onChange={update("education_training")}
                className={`${inputCls} resize-none`}
                placeholder="Degrees, CPE, chaplaincy training, certifications, or relevant coursework."
              />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Workplace or corporate chaplaincy experience">
              <textarea
                rows={3}
                data-testid="academy-corporate-experience"
                value={form.corporate_experience}
                onChange={update("corporate_experience")}
                className={`${inputCls} resize-none`}
                placeholder="Any workplace, employer, or corporate chaplaincy work."
              />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Areas of training interest">
              <textarea
                rows={3}
                data-testid="academy-training-interests"
                value={form.training_interests}
                onChange={update("training_interests")}
                className={`${inputCls} resize-none`}
                placeholder="e.g., grief and bereavement, boundaries, critical incident response, referral judgment."
              />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="General availability">
              <input
                data-testid="academy-availability"
                value={form.availability}
                onChange={update("availability")}
                className={inputCls}
                placeholder="e.g., evenings, weekends, weekday afternoons"
              />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Additional information">
              <textarea
                rows={3}
                data-testid="academy-additional-information"
                value={form.additional_information}
                onChange={update("additional_information")}
                className={`${inputCls} resize-none`}
                placeholder="Anything else you would like the review team to know."
              />
            </Field>
          </div>
          <div className="mt-5">
            <Field label={`Résumé (optional — PDF, DOC, or DOCX, max ${MAX_MB} MB)`}>
              <label className="flex items-center gap-3 cursor-pointer rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 hover:border-[#4A6FA5]">
                <Upload className="w-5 h-5 text-[#4A6FA5]" />
                <span className="text-sm text-slate-700">
                  {resume ? resume.name : "Choose file to upload"}
                </span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={onFileChange}
                  data-testid="academy-resume"
                  className="sr-only"
                />
              </label>
            </Field>
          </div>
        </div>

        {/* ---- FUTURE OPPORTUNITIES ---- */}
        <label className="flex items-start gap-3 text-sm text-slate-700 rounded-lg border border-slate-200 bg-slate-50 p-4 cursor-pointer">
          <input
            type="checkbox"
            checked={futureOptIn}
            onChange={(e) => setFutureOptIn(e.target.checked)}
            data-testid="academy-future-optin"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]"
          />
          <span>
            I would like to be considered for future California Corporate Chaplains opportunities.
          </span>
        </label>

        {/* ---- REQUIRED CONSENT ---- */}
        <label className="flex items-start gap-3 text-sm text-slate-700 pt-1">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            data-testid="academy-consent"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]"
          />
          <span>
            I understand that this Academy application does not constitute an employment application and does not guarantee Academy enrollment, employment, placement, assignment, or deployment.
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting}
          data-testid="academy-submit"
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60"
        >
          {submitting ? "Submitting…" : "Submit Academy Application"} <ArrowRight className="w-4 h-4" />
        </button>
      </form>

      <p className="mt-6 text-xs text-slate-500 leading-relaxed">
        Academy application, participation, or completion does not guarantee employment, placement, assignment, deployment, or a position with California Corporate Chaplains. The CCCHAPS Chaplain Training Academy does not claim accreditation, state approval, academic credit, professional certification, or licensure.
      </p>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-[#4A6FA5]">*</span>}
      </span>
      {children}
    </label>
  );
}
